import React, { useState } from "react";
import { Check, Loader2, RefreshCw, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { api, errMsg } from "@/lib/api";
import { WordDiff } from "@/components/canvas/WordDiff";

const quiet = "inline-flex h-7 items-center gap-1 rounded-[7px] px-2 text-[12.5px] font-medium text-foreground/70 transition-colors hover:bg-black/[0.06] hover:text-foreground disabled:opacity-40";
const solid = "inline-flex h-8 items-center gap-1.5 rounded-full bg-[hsl(var(--navy))] px-3.5 text-[13px] font-semibold text-white transition-colors hover:bg-[hsl(var(--ink-2))] disabled:opacity-40";

/** State for one proposal round: ask, read, accept or discard. Nothing is stored until `accept` runs the save. */
export function useFolgebrevForslag(caseId, onAccept) {
  const [state, setState] = useState({ status: "idle", data: null, error: null });
  const ask = async (instruction = "") => {
    setState((s) => ({ ...s, status: "loading", error: null }));
    try {
      const r = await api.post(`/projects/${caseId}/tilbud/folgebrev/forslag`, { instruction });
      setState({ status: "ready", data: r.data, error: null });
    } catch (e) {
      setState({ status: "error", data: null, error: errMsg(e) });
      toast.error(errMsg(e));
    }
  };
  const discard = () => setState({ status: "idle", data: null, error: null });
  const accept = async (text) => {
    setState((s) => ({ ...s, status: "saving" }));
    try {
      await onAccept(text);
      setState({ status: "idle", data: null, error: null });
      toast.success("Følgebrevet er tatt inn – du kan redigere det videre i dokumentet");
    } catch (e) {
      setState((s) => ({ ...s, status: "ready" }));
      toast.error(errMsg(e));
    }
  };
  return { ...state, ask, discard, accept, open: state.status !== "idle" };
}

/** The quiet trigger that sits with the cover letter: «Foreslå følgebrev» (or «Foreslå ny tekst» when one exists). */
export function FolgebrevTrigger({ f, hasText }) {
  return (
    <button type="button" onClick={() => f.ask()} disabled={f.status === "loading" || f.status === "saving"} className={cn(quiet, "text-[hsl(var(--navy))]")} title="Skriver et utkast fra kundens forespørsel og tilbudets oppbygning – til gjennomgang før det tas inn" data-testid="tilbud-folgebrev-propose">
      {f.status === "loading" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {hasText ? "Foreslå ny tekst" : "Foreslå følgebrev"}
    </button>
  );
}

/**
 * The review card: the proposed letter (as a word diff when a letter already exists), what it rests on, and three ways
 * out – take it in, ask for another round with an instruction, or discard. Nothing reaches the document before «Bruk teksten».
 */
export function FolgebrevForslag({ f }) {
  const [instruction, setInstruction] = useState("");
  const [text, setText] = useState(null);
  if (!f.open) return null;
  const d = f.data;
  const body = text ?? d?.text ?? "";
  const editing = text !== null;
  return (
    <section className="rounded-[12px] bg-[hsl(var(--navy)/0.05)] px-5 py-4 shadow-[0_0_0_1px_hsl(var(--navy)/0.12)]" data-testid="tilbud-folgebrev-forslag" data-status={f.status} aria-live="polite">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-[hsl(var(--navy))]"><Sparkles className="h-3.5 w-3.5" /> Forslag til følgebrev <span className="font-normal text-muted-foreground">· skrevet fra forespørselen og tilbudets oppbygning – ingenting er lagret</span></div>
        <button type="button" onClick={f.discard} className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-black/[0.06] hover:text-foreground" aria-label="Forkast forslaget" data-testid="tilbud-folgebrev-discard"><X className="h-3.5 w-3.5" /></button>
      </div>
      {f.status === "loading" && !d ? (
        <p className="mt-3 flex items-center gap-2 text-[14px] text-muted-foreground" data-testid="tilbud-folgebrev-loading"><Loader2 className="h-4 w-4 animate-spin" /> Leser forespørselen og tilbudets oppbygning …</p>
      ) : f.status === "error" ? (
        <p className="mt-3 text-[14px] text-[hsl(var(--v-blocked))]" data-testid="tilbud-folgebrev-error">{f.error}</p>
      ) : d ? (
        <>
          {editing ? (
            <textarea value={body} onChange={(e) => setText(e.target.value)} rows={Math.max(6, body.split("\n").length + 1)} className="mt-3 w-full resize-y rounded-[10px] border border-black/[0.1] bg-white px-3.5 py-2.5 text-[14.5px] leading-[1.6] outline-none focus:border-black/[0.25]" data-testid="tilbud-folgebrev-edit" />
          ) : d.current ? (
            <div className="mt-3 rounded-[10px] bg-white px-3.5 py-2.5" data-testid="tilbud-folgebrev-text"><WordDiff before={d.current} after={d.text} /></div>
          ) : (
            <div className="mt-3 whitespace-pre-wrap rounded-[10px] bg-white px-3.5 py-2.5 text-[14.5px] leading-[1.6]" data-testid="tilbud-folgebrev-text">{d.text}</div>
          )}
          {d.basis?.length ? <p className="mt-2.5 text-[12.5px] leading-[1.5] text-muted-foreground" data-testid="tilbud-folgebrev-basis"><span className="font-medium text-foreground/70">Bygger på:</span> {d.basis.join(" · ")}</p> : null}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => f.accept(body)} disabled={f.status === "saving" || !body.trim()} className={solid} data-testid="tilbud-folgebrev-accept">{f.status === "saving" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" strokeWidth={2.6} />} Bruk teksten</button>
            {!editing ? <button type="button" onClick={() => setText(d.text)} className={quiet} data-testid="tilbud-folgebrev-edit-first">Rediger først</button> : <button type="button" onClick={() => setText(null)} className={quiet} data-testid="tilbud-folgebrev-edit-cancel">Tilbake til forslaget</button>}
            <div className="ml-auto flex min-w-0 items-center gap-1">
              <input value={instruction} onChange={(e) => setInstruction(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && instruction.trim()) { f.ask(instruction.trim()); setText(null); } }} placeholder="Kortere · mer formelt · nevn opsjonene …" className="h-8 w-[220px] min-w-0 rounded-[8px] border border-black/[0.1] bg-white px-2.5 text-[13px] outline-none placeholder:text-muted-foreground/60 focus:border-black/[0.25]" aria-label="Instruks for nytt forslag" data-testid="tilbud-folgebrev-instruction" />
              <button type="button" onClick={() => { f.ask(instruction.trim()); setText(null); }} disabled={f.status === "loading"} className={quiet} title="Skriv et nytt forslag" data-testid="tilbud-folgebrev-again"><RefreshCw className={cn("h-3.5 w-3.5", f.status === "loading" && "animate-spin")} /> På nytt</button>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
